'use client';

import React from 'react';
import { Clock } from './Money';

export interface TimeSlotChipProps {
  time: string;
  selected?: boolean;
  /** Edge-case only — prefer OMITTING unavailable times entirely. */
  disabled?: boolean;
  onClick?: () => void;
  /** Override the accessible name (default: the visible time) — e.g. to add
   * context like "17:30 — reschedule" when the chip's action isn't obvious
   * from the visible time alone. */
  'aria-label'?: string;
  style?: React.CSSProperties;
}

/** Time-slot chip. Selected fills accent. Unavailable times should be OMITTED, not greyed. */
export function TimeSlotChip({
  time,
  selected = false,
  disabled = false,
  onClick,
  'aria-label': ariaLabel,
  style = {},
}: TimeSlotChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      aria-label={ariaLabel}
      style={{
        minWidth: 76,
        height: 44,
        paddingInline: 'var(--sp-4)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--fs-label)',
        fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'],
        color: selected ? 'var(--on-brand)' : 'var(--text-primary)',
        background: selected ? 'var(--brand)' : 'var(--bg-surface)',
        border: `1px solid ${selected ? 'transparent' : 'var(--border-strong)'}`,
        borderRadius: 'var(--r-sm)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        transition:
          'background var(--dur-micro), color var(--dur-micro), transform var(--dur-micro)',
        ...style,
      }}
    >
      <Clock value={time} />
    </button>
  );
}
